import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Chip,
  Alert,
  Typography,
  Pagination,
  Switch,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Visibility as VisibilityIcon, Search as SearchIcon } from '@mui/icons-material';
import axios from 'axios';

interface FeeInstallmentModel {
  id: string;
  name: string;
  sequenceOrder: number;
  dueDate: string;
  allowLateFee: boolean;
  isActive: boolean;
}

const PAGE_SIZE = 10;

const FeeInstallmentMaster: React.FC = () => {
  const [installments, setInstallments] = useState<FeeInstallmentModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    sequenceOrder: 1,
    dueDate: '',
    allowLateFee: false,
    isActive: true,
  });
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    const list = s
      ? installments.filter((i) => i.name.toLowerCase().includes(s))
      : installments;
    return list.sort((a, b) => a.sequenceOrder - b.sequenceOrder);
  }, [installments, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pagedData = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  useEffect(() => {
    fetchInstallments();
  }, []);

  const fetchInstallments = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get('/system-settings/fee-installment');
      setInstallments(res.data);
    } catch (err) {
      setError('Failed to fetch fee installments');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingId(null);
    setViewMode(false);
    setFormData({
      name: '',
      sequenceOrder: (installments.at(-1)?.sequenceOrder ?? 0) + 1,
      dueDate: '',
      allowLateFee: false,
      isActive: true,
    });
    setDialogOpen(true);
  };

  const handleEdit = (inst: FeeInstallmentModel) => {
    setEditingId(inst.id);
    setViewMode(false);
    setFormData({
      name: inst.name,
      sequenceOrder: inst.sequenceOrder,
      dueDate: inst.dueDate?.slice(0, 10),
      allowLateFee: inst.allowLateFee,
      isActive: inst.isActive,
    });
    setDialogOpen(true);
  };

  const handleView = (inst: FeeInstallmentModel) => {
    setEditingId(inst.id);
    setViewMode(true);
    setFormData({
      name: inst.name,
      sequenceOrder: inst.sequenceOrder,
      dueDate: inst.dueDate?.slice(0, 10),
      allowLateFee: inst.allowLateFee,
      isActive: inst.isActive,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this installment?')) return;
    try {
      await axios.delete(`/system-settings/fee-installment/${id}`);
      fetchInstallments();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Cannot delete installment');
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      await axios.patch(`/system-settings/fee-installment/${id}/toggle-status`);
      setInstallments((prev) =>
        prev.map((inst) =>
          inst.id === id ? { ...inst, isActive: !currentStatus } : inst
        )
      );
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to toggle status');
      fetchInstallments();
    }
  };

  const handleSave = async () => {
    try {
      const payload = { ...formData };
      if (editingId && !viewMode) {
        await axios.patch(`/system-settings/fee-installment/${editingId}`, payload);
      } else if (!editingId) {
        await axios.post('/system-settings/fee-installment', payload);
      }
      setDialogOpen(false);
      fetchInstallments();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to save installment');
    }
  };

  useEffect(() => {
    setPage(1);
  }, [search]);

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Fee Installments</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search installments"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon fontSize="small" /> }}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
            Create
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Installment Name</TableCell>
              <TableCell>Sequence</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell>Late Fee Allowed</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center"><CircularProgress /></TableCell>
              </TableRow>
            ) : pagedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">No installments found.</TableCell>
              </TableRow>
            ) : (
              pagedData.map((inst) => (
                <TableRow key={inst.id}>
                  <TableCell>{inst.name}</TableCell>
                  <TableCell>{inst.sequenceOrder}</TableCell>
                  <TableCell>{inst.dueDate?.slice(0, 10)}</TableCell>
                  <TableCell>{inst.allowLateFee ? 'Yes' : 'No'}</TableCell>
                  <TableCell>
                    <Switch
                      checked={inst.isActive}
                      onChange={() => handleToggleStatus(inst.id, inst.isActive)}
                      color="primary"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleView(inst)}><VisibilityIcon /></IconButton>
                    <IconButton size="small" onClick={() => handleEdit(inst)}><EditIcon /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete(inst.id)}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} />
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{viewMode ? 'View Installment' : editingId ? 'Edit Installment' : 'New Installment'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1, minWidth: 320 }}>
            <TextField
              label="Installment Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              disabled={viewMode}
            />
            <TextField
              label="Sequence Order"
              type="number"
              value={formData.sequenceOrder}
              onChange={(e) => setFormData({ ...formData, sequenceOrder: Number(e.target.value) })}
              fullWidth
              required
              disabled={viewMode}
            />
            <TextField
              label="Due Date"
              type="date"
              value={formData.dueDate}
              onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
              fullWidth
              required
              disabled={viewMode}
              InputLabelProps={{ shrink: true }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.allowLateFee}
                  onChange={(e) => setFormData({ ...formData, allowLateFee: e.target.checked })}
                  disabled={viewMode}
                />
              }
              label="Allow Late Fee"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  disabled={viewMode}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
          {!viewMode && <Button onClick={handleSave} variant="contained">Save</Button>}
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default FeeInstallmentMaster;
